import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { IDefaultReducerState } from 'types/state.interface';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import type { TFieldRelation } from '@timeedit/types';
import api from '../services/api.service';

type TFieldRelationsState = IDefaultReducerState & {
  firstFieldRelation: TFieldRelation | null;
  secondFieldRelation: TFieldRelation | null;
  allFieldRelations: TFieldRelation[];
  fieldRelationMapping: Record<number, TFieldRelation>;
};

const initialState: TFieldRelationsState = {
  loading: false,
  hasErrors: false,
  firstFieldRelation: null,
  secondFieldRelation: null,
  allFieldRelations: [],
  fieldRelationMapping: {},
};

// Slice
const slice = createSlice({
  name: 'fieldRelations',
  initialState,
  reducers: {
    fetchFieldRelationsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchFieldRelationsSuccess: (
      state,
      { payload }: PayloadAction<{ results: TFieldRelation[] }>
    ) => {
      state.allFieldRelations = payload.results;
      state.fieldRelationMapping = _.keyBy(payload.results, 'id');
      finishedLoadingSuccess(state);
    },
    fetchFieldRelationsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createFieldRelationRequest: (state) => {
      isLoadingRequest(state);
    },
    createFieldRelationSuccess: (state) => {
      state.firstFieldRelation = undefined;
      finishedLoadingSuccess(state);
    },
    createUnsavedFieldRelationSuccess: (
      state,
      { payload }: PayloadAction<TFieldRelation>
    ) => {
      if (state.firstFieldRelation?.id === 0) return;
      state.fieldRelationMapping[payload.id] = payload;
      state.allFieldRelations.unshift(payload);
      state.firstFieldRelation = payload;
    },
    createFieldRelationFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setFirstFieldRelation: (
      state,
      { payload }: PayloadAction<TFieldRelation>
    ) => {
      state.firstFieldRelation = payload;
      // If closed field relation, remove new reservation from list
      if (!payload) {
        state.allFieldRelations = state.allFieldRelations.filter(
          (fr: TFieldRelation) => fr.id !== 0
        );
      }
    },
    setSecondFieldRelation: (
      state,
      { payload }: PayloadAction<TFieldRelation>
    ) => {
      state.secondFieldRelation = payload;
    },
    deleteFieldRelationRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteFieldRelationSuccess: (
      state,
      { payload }: PayloadAction<{ fieldRelationId: number }>
    ) => {
      state.allFieldRelations = state.allFieldRelations.filter(
        (fieldRelation: TFieldRelation) =>
          fieldRelation.id !== payload.fieldRelationId
      );
      if (state.firstFieldRelation?.id === payload.fieldRelationId) {
        state.firstFieldRelation = undefined;
      } else {
        state.secondFieldRelation = undefined;
      }
      finishedLoadingSuccess(state);
    },
    deleteFieldRelationFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateFieldRelationRequest: (state) => {
      isLoadingRequest(state);
    },
    updateFieldRelationSuccess: (
      state,
      {
        payload: { fieldRelation },
      }: PayloadAction<{ fieldRelation: TFieldRelation }>
    ) => {
      const index = state.allFieldRelations.findIndex(
        (fr: TFieldRelation) => fr.id === fieldRelation.id
      );
      state.allFieldRelations[index] = fieldRelation;
      state.fieldRelationMapping[fieldRelation.id] = fieldRelation;
      if (state.firstFieldRelation?.id === fieldRelation.id) {
        state.firstFieldRelation = undefined;
      } else {
        state.secondFieldRelation = undefined;
      }
      finishedLoadingSuccess(state);
    },
    updateFieldRelationFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const fieldRelationsLoading = (state: TRootState) =>
  state.fieldRelations.loading;
export const fieldRelationsSelector = (state: TRootState) =>
  state.fieldRelations.allFieldRelations;
export const firstFieldRelationSelector = (state: TRootState) => {
  return state.fieldRelations.firstFieldRelation;
};
export const secondFieldRelationSelector = (state: TRootState) => {
  return state.fieldRelations.secondFieldRelation;
};
export const fieldRelationMappingSelector = (state: TRootState) =>
  state.fieldRelations.fieldRelationMapping;
export const selectedFieldRelationIdsSelector = (state: TRootState) =>
  _.compact([
    state.fieldRelations.firstFieldRelation,
    state.fieldRelations.secondFieldRelation,
  ]).map((fr) => fr.id);

// Actions
export const {
  fetchFieldRelationsRequest,
  fetchFieldRelationsSuccess,
  fetchFieldRelationsFailure,
  createFieldRelationRequest,
  createFieldRelationSuccess,
  createUnsavedFieldRelationSuccess,
  createFieldRelationFailure,
  setFirstFieldRelation,
  setSecondFieldRelation,
  deleteFieldRelationRequest,
  deleteFieldRelationSuccess,
  deleteFieldRelationFailure,
  updateFieldRelationRequest,
  updateFieldRelationSuccess,
  updateFieldRelationFailure,
} = slice.actions;

export const fetchFieldRelations = createAppAsyncThunk(
  'fieldRelations/fetchFieldRelations',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchFieldRelationsRequest());
      const fieldRelations = await api.get({ endpoint: `/field-relations` });
      dispatch(fetchFieldRelationsSuccess(fieldRelations));
    } catch (e) {
      dispatch(fetchFieldRelationsFailure());
      return console.error(e);
    }
  }
);

export const createFieldRelation = createAppAsyncThunk<void, TFieldRelation>(
  'fieldRelations/createFieldRelation',
  async (fieldRelation, { dispatch }) => {
    try {
      dispatch(createFieldRelationRequest());
      const { id, history, ...fieldRelationBody } = fieldRelation;
      await api.post({
        endpoint: `/field-relations`,
        data: fieldRelationBody,
      });
      dispatch(createFieldRelationSuccess());
      dispatch(fetchFieldRelations());
    } catch (e: any) {
      dispatch(createFieldRelationFailure(_.get(e, 'e.response.data.message')));
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const createUnsavedFieldRelation = (): TAppThunk => async (dispatch) => {
  const response: TFieldRelation = {
    id: 0,
    name: '',
    description: 'New field relation',
    documentation: '',
    history: [],
    reservationTemplates: [],
    sourceField: null,
  };
  dispatch(createUnsavedFieldRelationSuccess(response));
};

export const copyFieldRelation = createAppAsyncThunk<void, TFieldRelation>(
  'fieldRelations/copyFieldRelation',
  async (fieldRelation, { dispatch }) => {
    const response = {
      ...fieldRelation,
      id: 0,
      description: '',
      documentation: '',
      reservationTemplates: [],
      name: fieldRelation.name + '-copy',
    };
    dispatch(createUnsavedFieldRelationSuccess(response));
  }
);

export const deleteFieldRelation = createAppAsyncThunk<void, number>(
  'fieldRelations/deleteFieldRelation',
  async (fieldRelationId, { dispatch }) => {
    try {
      dispatch(deleteFieldRelationRequest());
      await api.delete({
        endpoint: `/field-relations/${fieldRelationId}`,
      });
      dispatch(deleteFieldRelationSuccess({ fieldRelationId }));
    } catch (e: any) {
      dispatch(deleteFieldRelationFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const updateFieldRelation = createAppAsyncThunk<void, TFieldRelation>(
  'fieldRelations/updateFieldRelation',
  async (fieldRelation, { dispatch }) => {
    try {
      dispatch(updateFieldRelationRequest());
      const { id, history, ...fieldRelationBody } = fieldRelation;
      const response = await api.patch({
        endpoint: `/field-relations/${fieldRelation.id}`,
        data: { ...fieldRelationBody },
      });
      dispatch(
        updateFieldRelationSuccess({
          fieldRelation: response,
        })
      );
    } catch (e: any) {
      dispatch(updateFieldRelationFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const checkFieldRelationSaveDisabled =
  (isFirstDrawer: boolean): TAppThunk<boolean> =>
  (_, getState) => {
    const { fieldRelations } = getState();
    const { firstFieldRelation, secondFieldRelation, fieldRelationMapping } =
      fieldRelations;
    if (isFirstDrawer && firstFieldRelation) {
      return checkSavedDisabled(
        firstFieldRelation,
        fieldRelationMapping[firstFieldRelation.id]
      );
    }
    if (!isFirstDrawer && secondFieldRelation) {
      return checkSavedDisabled(
        secondFieldRelation,
        fieldRelationMapping[secondFieldRelation.id]
      );
    }
    return true;
  };

export const checkIfFirstFieldRelationExists =
  (): TAppThunk<boolean> => (_, getState) => {
    const { fieldRelations } = getState();
    return !!fieldRelations.firstFieldRelation;
  };
